import React, { useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useThemeMode } from '../../design-system/theme';
import useABTest from '../../hooks/useABTest';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import {
  EducationDesignPreview,
  normalizeEducationDesignVariant,
} from '../education/EducationDesignExperiment';
import NextChallengePage from './NextChallengePage';
import { CHALLENGE_AD_MATCH_COPY } from './challengeAdMatchCopy';
import { ChallengeFormFirstCopyContext } from './challengeFormFirstCopy';
import {
  CHALLENGE_ABC_EXPERIMENT,
  CHALLENGE_CLARITY_HOME_EXPERIMENT,
  decideChallengeAssignment,
  normalizeChallengeDesign,
  type ChallengeDesignArm,
  type ChallengeExperimentContext,
  type ChallengeFlagReading,
} from './challengePageExperiment';
import './ChallengePageClarityHome.css';

// The JavaScript hook exposes this stable contract; its legacy JSDoc returns Object.
// Keep the boundary explicit, as in EducationDesignExperiment.
type ChallengeAssignment = ChallengeFlagReading & { isPreviewOverride: boolean };

/** Arms painted on B's form-first page. C differs from B only in its copy. */
const FORM_FIRST_ARMS: readonly ChallengeDesignArm[] = ['form_first', 'form_first_ad_match'];

/** A new assignment replaces the old design wrapper atomically, never its history. */
export default function ChallengeClarityHomeExperiment({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const location = useLocation();
  const [theme] = useThemeMode();
  const isTarget = location.pathname.replace(/\/+$/, '') === '/challenges';
  const previewHost = isTarget && isPreviewExperimentOverrideHost();
  const searchParams = new URLSearchParams(location.search);
  const preview = previewHost
    ? normalizeChallengeDesign(searchParams.get('challengeDesign'))
    : null;
  // An explicit design preview of the existing experiment (?educationDesign=)
  // goes straight to that route. It never waits on these flags.
  const existingRoutePreview =
    previewHost && !preview && Boolean(searchParams.get('educationDesign'));
  const skipAll = !isTarget || Boolean(preview) || existingRoutePreview;
  const forcedOff = !isTarget || existingRoutePreview ? 'control' : undefined;

  // Rob, 2026-10-02: the A/B/C test runs on its own new flag, read first. A
  // preview of one of its arms is applied here without a flag read.
  const abcPreview = CHALLENGE_ABC_EXPERIMENT.posthogVariants.find((arm) => arm === preview);
  const abcTracking = useMemo(
    () => ({
      page: location.pathname,
      challenge_path: location.pathname,
      surface: 'challenge_landing_page',
      education_theme: theme,
      validVariants: CHALLENGE_ABC_EXPERIMENT.posthogVariants,
      skipFeatureFlagEvaluation: skipAll,
      forcedVariant: forcedOff || (preview ? abcPreview || 'control' : undefined),
      trackForcedExposure: false,
    }),
    [location.pathname, theme, skipAll, forcedOff, preview, abcPreview],
  );
  // SAFETY: useABTest returns these four fields on every path; the JS hook
  // normalizes variants to string, boolean, or null and supplies boolean state.
  const abc = useABTest(
    CHALLENGE_ABC_EXPERIMENT.name,
    CHALLENGE_ABC_EXPERIMENT.featureFlag,
    'control',
    abcTracking,
  ) as ChallengeAssignment;
  const abcDecision = decideChallengeAssignment(abc, {
    variant: null,
    isLoading: false,
    isExperimentActive: false,
  });

  // Martin's flag decides exactly as before, but only once the A/B/C flag has
  // answered without one of its arms. Until then it is held on control with no
  // read and no exposure, so a visitor the new test takes is never counted in
  // Martin's experiment.
  const originalReadEnabled = !skipAll && abcDecision.status === 'unassigned';
  const originalTracking = useMemo(
    () => ({
      page: location.pathname,
      challenge_path: location.pathname,
      surface: 'challenge_landing_page',
      education_theme: theme,
      validVariants: CHALLENGE_CLARITY_HOME_EXPERIMENT.posthogVariants,
      skipFeatureFlagEvaluation: skipAll || !originalReadEnabled,
      forcedVariant: forcedOff || preview || (originalReadEnabled ? undefined : 'control'),
      trackForcedExposure: false,
    }),
    [location.pathname, theme, skipAll, forcedOff, preview, originalReadEnabled],
  );
  // SAFETY: as above, useABTest returns these four fields on every path.
  const original = useABTest(
    CHALLENGE_CLARITY_HOME_EXPERIMENT.name,
    CHALLENGE_CLARITY_HOME_EXPERIMENT.featureFlag,
    'control',
    originalTracking,
  ) as ChallengeAssignment;
  // The render in which Martin's flag is first allowed to read still shows the
  // held control answer from before. Treat it as still answering so a held
  // value is never reported as his assignment.
  const originalWasEnabled = useRef<boolean | null>(null);
  const originalJustEnabled = originalWasEnabled.current === false && originalReadEnabled;
  originalWasEnabled.current = originalReadEnabled;
  const decision = decideChallengeAssignment(abc, {
    ...original,
    isLoading: original.isLoading || originalJustEnabled,
  });

  // True when this page first rendered before PostHog answered. That visitor
  // has had a usable page from the start and keeps it (see below).
  const renderedWhileResolving = useRef(decision.status === 'resolving' && !preview).current;
  if (!isTarget || existingRoutePreview) return <>{children}</>;
  // Rob, 2026-09-23: "The register button should be ready from the moment the
  // page loads." While PostHog answers, which can take seconds on a slow phone
  // or behind an ad blocker, render the original page with a working form, not
  // a loading screen. It is the same tree every arm below renders, so when the
  // assignment lands on any arm the page component, and what the visitor
  // typed, stays. The old experiment is still never mounted before this one
  // resolves. If neither flag then serves an arm, the visitor keeps the
  // original page they already have rather than having it swapped for the
  // existing route (whose flag, 459166, is off, so it shows the same design).
  const resolving = decision.status === 'resolving' && !preview;
  const live = decision.status === 'assigned' ? decision : null;
  const assigned: ChallengeDesignArm | null = preview || (live ? live.arm : null);
  if (!assigned && !resolving && !renderedWhileResolving) return <>{children}</>;
  // A control preview on localhost or Dev still has to honor educationDesign.
  // Live control keeps the replacement page. Clarity Home stays the test arm.
  const educationDesignPreview =
    preview === 'control'
      ? normalizeEducationDesignVariant(searchParams.get('educationDesign'))
      : null;
  if (educationDesignPreview) return <>{children}</>;
  const design: ChallengeDesignArm = assigned || 'control';
  const active = Boolean(live && !preview && !live.isPreviewOverride);
  // Every Lead and PostHog event from the page spreads this, so each one says
  // which arm the visitor saw and which flag decided it.
  const decidingFlag = live && active ? live.experiment.featureFlag : null;
  let experimentContext: ChallengeExperimentContext | undefined;
  if (live && decidingFlag) {
    experimentContext = {
      experiment_name: live.experiment.name,
      experiment_variant: design,
      experiment_flag: decidingFlag,
      feature_flag: decidingFlag,
      is_experiment_active: true,
    };
    experimentContext[`$feature/${decidingFlag}`] = design;
  }
  return (
    <div
      className={`education-design-experiment${design === 'test' ? ' challenge-home-review' : ''}`}
      data-testid="challenge-clarity-home-experiment"
      data-challenge-design={design}
      data-assignment={resolving ? 'resolving' : 'resolved'}
      data-experiment-active={String(active)}
      data-experiment-flag={decidingFlag || undefined}
      data-education-theme={theme}
      data-bs-theme={theme}
    >
      <EducationDesignPreview variant={FORM_FIRST_ARMS.includes(design) ? 'form_first' : 'control'}>
        {/* C (form_first_ad_match) is B's page with the top Proven ad's heading
            and subheading (Rob, 2026-10-02). B and every other arm get null,
            which keeps their own copy. */}
        <ChallengeFormFirstCopyContext.Provider
          value={design === 'form_first_ad_match' ? CHALLENGE_AD_MATCH_COPY : null}
        >
          <NextChallengePage
            presentation={design === 'test' ? 'clarity-home' : undefined}
            experimentContext={experimentContext}
          />
        </ChallengeFormFirstCopyContext.Provider>
      </EducationDesignPreview>
    </div>
  );
}
